import { clientSDK } from "@istari-frontend/api-client";

import { assertFileAdmission, validateContentLength } from "~/resource/core/file-transfer-policy";
import { fileTransferConfig, readContentQueue } from "~/resource/data-source/file-transfer-config";

export class ContentRepresentationError extends Error {
  constructor() {
    super("Content uses an unsupported byte representation.");
    this.name = "ContentRepresentationError";
  }
}

export type ContentReadPurpose = "preview" | "diff" | "download" | "zip";

export interface BufferedContentMetadata {
  name?: string | null;
  displayName?: string | null;
  size?: number | null;
  contentToken?: { sha: string; salt: string } | null;
  propertiesToken?: { sha: string; salt: string } | null;
}

export async function readBufferedContent(
  revision: BufferedContentMetadata,
  purpose: ContentReadPurpose,
  signal?: AbortSignal,
): Promise<Uint8Array<ArrayBuffer>> {
  const file = { name: revision.displayName || revision.name || "File", size: revision.size ?? Number.NaN };
  const limit = contentLimit(purpose);
  assertFileAdmission(file, limit);
  if (!revision.contentToken) throw new Error("Revision content not available");
  const { sha, salt } = revision.contentToken;
  signal?.throwIfAborted();
  return readContentQueue.run(async () => {
    signal?.throwIfAborted();
    const content: Uint8Array<ArrayBufferLike> = await clientSDK.readContent(
      sha,
      salt,
      ...(signal ? [{ signal }] : []),
    );
    signal?.throwIfAborted();
    validateContentLength(content, file, limit);
    if (content.buffer instanceof ArrayBuffer) {
      return new Uint8Array(content.buffer, content.byteOffset, content.byteLength);
    }
    if (typeof SharedArrayBuffer !== "undefined" && content.buffer instanceof SharedArrayBuffer) {
      return new Uint8Array(content);
    }
    throw new ContentRepresentationError();
  }, signal);
}

function contentLimit(purpose: ContentReadPurpose): number {
  switch (purpose) {
    case "preview":
      return fileTransferConfig.previewMaxBytes;
    case "diff":
      return fileTransferConfig.previewMaxBytes;
    case "download":
      return fileTransferConfig.downloadMaxBytes;
    case "zip":
      return fileTransferConfig.zipMaxBytes;
  }
}
